import { ApplicationDocumentModel } from '../../../models/ApplicationDocumentModel'
import { ApplicationModel } from '../../../models/ApplicationModel'
import { ApplicationTiers, BusinessCategoriesReform } from '../../../shared/Utility'

export interface DocumentTypeInfo {
    code: string
    folderName: string
    reducer: { (model: ApplicationModel): string[] }
    title: { (model: ApplicationModel, item: string): string }
    description: { (model: ApplicationModel, item: string): string }
    required: { (model: ApplicationModel, item: string): boolean }
    quickList: { (model: ApplicationModel, item: string): boolean }
    multipleFiles: { (model: ApplicationModel, item: string): boolean }
    mlClasses: string[]
    moreInfo?: string
}

export const isDocumentPendingScoring = (document: ApplicationDocumentModel): boolean => {
  const docType = documentTypeInfo.filter((r) => r.code === document.code)

  if (docType.length > 0) {
    if (docType[0].mlClasses == null) return false

    return document.mlScore == null
  }

  return false
}

export const CODE_INFECTED = 'infected'

export const isDocumentValid = (document: ApplicationDocumentModel): boolean => {
  const docType = documentTypeInfo.filter((r) => r.code === document.code)

  if (docType.length > 0) {
    if (docType[0].mlClasses == null) return true

    return docType[0].mlClasses.some((x) => x === document.mlCode)
  }

  return true
}

export const isDocumentInfected = (document: ApplicationDocumentModel): boolean => document.mlCode === CODE_INFECTED

const profitLossBalaceSheetReqiredForRepBodies = (m: ApplicationModel): boolean => (m.isPeakIndustryBody && m.doDistributeIncomeToSmeMembers === false)

const profitLossBalaceSheetReqiredForTiers = (m: ApplicationModel): boolean => (!m.isPeakIndustryBody && m.totalTurnover !== null)

export const documentTypeInfo: DocumentTypeInfo[] = [
  // Declaration
  {
    code: 'trust_entity',
    folderName: '03 Applicant Eligibility',
    reducer: (m) => (m.businessCategory === BusinessCategoriesReform.Trust ? [''] : []),
    title: (m) => 'Trust deed including any amendments (if applicable)',
    description: (m) => 'Trust deed including any amendments (if applicable)',
    required: (m) => true,
    quickList: (m) => true,
    multipleFiles: (m) => true,
    mlClasses: ['trust_entity', 'eligible_applicant'],
  },
  {
    code: 'export_readiness_evidence',
    folderName: 'Export Readiness',
    reducer: (m) => (m.exportReadinessEvidence ? [''] : []),
    title: (m) => 'Export readiness evidence',
    description: (m) => 'Export readiness evidence',
    required: (m) => m.exportReadinessEvidence === true,
    quickList: (m) => true,
    multipleFiles: (m) => true,
    mlClasses: ['evidence_export_readiness', 'eligible_tier'],
  },
  {
    code: 'total_turnover_profit_and_loss_1st_fy',
    folderName: '02 Financials',
    reducer: (m) => (profitLossBalaceSheetReqiredForTiers(m) || profitLossBalaceSheetReqiredForRepBodies(m) ? [''] : []),
    title: (m) => (profitLossBalaceSheetReqiredForRepBodies(m) ? 'Profit & Loss statement' : 'Profit & Loss statement (first financial year)'),
    description: (m) => (profitLossBalaceSheetReqiredForRepBodies(m) ? 'Profit & Loss statement' : 'Profit & Loss statement (first financial year)'),
    required: (m) => true,
    quickList: (m) => true,
    multipleFiles: (m) => true,
    mlClasses: ['total_turnover_profit_and_loss'],
  },
  {
    code: 'total_turnover_profit_and_loss_2nd_fy',
    folderName: '02 Financials',
    reducer: (m) => (profitLossBalaceSheetReqiredForTiers(m) ? [''] : []),
    title: (m) => 'Profit & Loss statement (second financial year)',
    description: (m) => 'Profit & Loss statement (second financial year)',
    required: (m) => true,
    quickList: (m) => true,
    multipleFiles: (m) => true,
    mlClasses: ['total_turnover_profit_and_loss'],
  },
  {
    code: 'total_turnover_balance_sheet_1st_fy',
    folderName: '02 Financials',
    reducer: (m) => (profitLossBalaceSheetReqiredForTiers(m) || profitLossBalaceSheetReqiredForRepBodies(m) ? [''] : []),
    title: (m) => (profitLossBalaceSheetReqiredForRepBodies(m) ? 'Balance sheet' : 'Balance sheet (first financial year)'),
    description: (m) => (profitLossBalaceSheetReqiredForRepBodies(m) ? 'Balance sheet' : 'Balance sheet (first financial year)'),
    required: (m) => true,
    quickList: (m) => true,
    multipleFiles: (m) => true,
    mlClasses: ['total_turnover_balance_sheet'],
  },
  {
    code: 'total_turnover_balance_sheet_2nd_fy',
    folderName: '02 Financials',
    reducer: (m) => (profitLossBalaceSheetReqiredForTiers(m) ? [''] : []),
    title: (m) => 'Balance sheet (second financial year)',
    description: (m) => 'Balance sheet (second financial year)',
    required: (m) => true,
    quickList: (m) => true,
    multipleFiles: (m) => true,
    mlClasses: ['total_turnover_balance_sheet'],
  },
  {
    code: 'evidence_of_tax_compliance_1',
    folderName: '02 Financials',
    reducer: (m) => (m.acceptATOComplianceDeclaration ? [''] : []),
    title: (m) => 'Tax compliance document (1)',
    description: (m) => 'Tax compliance document (1)',
    required: (m) => true,
    quickList: (m) => true,
    multipleFiles: (m) => true,
    mlClasses: ['tax_compliance'],
  },
  {
    code: 'evidence_of_tax_compliance_2',
    folderName: '02 Financials',
    reducer: (m) => (m.acceptATOComplianceDeclaration ? [''] : []),
    title: (m) => 'Tax compliance document (2)',
    description: (m) => 'Tax compliance document (2)',
    required: (m) => true,
    quickList: (m) => true,
    multipleFiles: (m) => true,
    mlClasses: ['tax_compliance'],
  },
  {
    code: 'current_bank_statement',
    folderName: '02 Financials',
    reducer: (m) => (m.declarationHaveAndMatchMinimumGrant ? [''] : []),
    title: (m) => 'Current bank statement',
    description: (m) => 'Current bank statement',
    required: (m) => true,
    quickList: (m) => true,
    multipleFiles: (m) => true,
    mlClasses: ['current_bank_statement', 'eligible_expenses'],
  },
  {
    code: 'account_transations_nov',
    folderName: '02 Financials',
    reducer: (m) => (m.declarationHaveAndMatchMinimumGrant ? [''] : []),
    title: (m) => 'Bank account transactions (November)',
    description: (m) => 'Bank account transactions (November)',
    required: (m) => true,
    quickList: (m) => true,
    multipleFiles: (m) => true,
    mlClasses: ['account_transations_nov', 'eligible_expenses'],
  },
  {
    code: 'plan_to_market',
    folderName: '04 Product Eligibility',
    reducer: (m) => (m.haveExportPlan ? [''] : []),
    title: (m) => 'Plan To Market',
    description: (m) => 'Plan To Market',
    required: (m) => true,
    quickList: (m) => true,
    multipleFiles: (m) => true,
    mlClasses: ['plan_to_market', 'eligible_product'],
  },
  {
    code: 'goods_made_outside_Australia',
    folderName: '04 Product Eligibility',
    reducer: (m) => (m.goodsSatisfyNonAustralianCriteria ? [''] : []),
    title: (m) => 'Goods made outside Australia',
    description: (m) => 'Goods made outside Australia',
    required: (m) => true,
    quickList: (m) => true,
    multipleFiles: (m) => true,
    mlClasses: ['goods_made_outside_Australia', 'eligible_product'],
  },
  {
    code: 'services_eligibility',
    folderName: '04 Product Eligibility',
    reducer: (m) => (m.servicesEligibilityCriteria ? [''] : []),
    title: (m) => 'Services other than tourism',
    description: (m) => 'Services other than tourism',
    required: (m) => true,
    quickList: (m) => true,
    multipleFiles: (m) => true,
    mlClasses: ['services_eligibility', 'eligible_product'],
  },
  {
    code: 'evidence_of_exporting_1',
    folderName: '05 Tier Eligibility',
    reducer: (m) => (m.hasEverExported && (m.applicationTier === ApplicationTiers.Tier2 || m.applicationTier === ApplicationTiers.Tier3) ? [''] : []),
    title: () => 'Exporting evidence document (1)',
    description: () => 'Exporting evidence document (1)',
    required: () => true,
    quickList: () => true,
    multipleFiles: () => true,
    mlClasses: ['evidence_of_exporting_1', 'eligible_tier'],
  },
  {
    code: 'evidence_of_exporting_2',
    folderName: '05 Tier Eligibility',
    reducer: (m) => (m.hasEverExported && (m.applicationTier === ApplicationTiers.Tier2 || m.applicationTier === ApplicationTiers.Tier3) ? [''] : []),
    title: () => 'Exporting evidence document (2)',
    description: () => 'Exporting evidence document (2)',
    required: () => true,
    quickList: () => true,
    multipleFiles: () => true,
    mlClasses: ['evidence_of_exporting_2', 'eligible_tier'],
  },
  {
    code: 'memorandum_of_incorporation',
    folderName: '03 Applicant Eligibility',
    reducer: (m) => (m.isPeakIndustryBody && (m.expenseAreExpandingExportPromotion || m.expenseEligibleExpensesTraining) ? [''] : []),
    title: () => 'Memorandum of incorporation, articles of association or constitution',
    description: () => 'Memorandum of incorporation, articles of association or constitution',
    required: () => true,
    quickList: () => true,
    multipleFiles: () => true,
    mlClasses: ['memorandum_of_incorporation', 'eligible_tier'],
  },
  {
    code: 'export_training_plan',
    folderName: '03 Applicant Eligibility',
    reducer: (m) => (m.isPeakIndustryBody && m.expenseEligibleExpensesTraining ? [''] : []),
    title: () => 'Export training plan',
    description: () => 'Export training plan',
    required: () => true,
    quickList: () => true,
    multipleFiles: () => true,
    mlClasses: ['export_training_plan', 'eligible_tier'],
  },
]
